import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDataWAuth, postDataWAuth } from "../../../utils/api-service";
import { DASHBOARD_API } from "../../../constants/dashboardConstants";
import {
  USER_PREFERENCE,
  UPDATE_DASHBOARD_PREFERENCE,
  UPDATE_DEALER_PREFERENCE,
} from "../../../constants/constant";

export const fetchBPDashboardData = createAsyncThunk(
  "bpDashboard/fetchBPDashboardData",
  async (countries, { rejectWithValue }) => {
    try {
      let url = DASHBOARD_API.BP_DASHBOARD;
      if (countries && countries.length > 0) {
        const countryParams = countries.map((country) => `countries=${country}`).join("&");
        url = `${DASHBOARD_API.BP_DASHBOARD}?${countryParams}`;
      }
      const response = await getDataWAuth(url);

      const backendData = response.data;

      return {
        extensionRequests: (backendData.extensionRequests || []).map((item, index) => ({
          id: index + 1,
          country: item.country,
          bp: item.bp ?? 0,
          ccm: item.ccm ?? 0,
        })),
        pendingErrors: (backendData.pendingErrors || []).map((item, index) => ({
          id: index + 1,
          country: item.country,
          errors: item.errors ?? 0,
        })),
        pendingBPCheck: (backendData.pendingBPCheck || []).map((item, index) => ({
          id: index + 1,
          country: item.country,
          pending: item.pending ?? 0,
        })),
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch BP dashboard data");
    }
  }
);

export const fetchDashboardCountryPreferences = createAsyncThunk(
  "bpDashboard/fetchDashboardCountryPreferences",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDataWAuth(USER_PREFERENCE);
      const preferedDashboardCountries = response?.data?.data?.preferedDashboardCountries;

      if (preferedDashboardCountries) {
        return preferedDashboardCountries.split(",");
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch country preferences");
    }
  }
);

export const updateDashboardCountryPreferences = createAsyncThunk(
  "bpDashboard/updateDashboardCountryPreferences",
  async (countryCodes, { rejectWithValue }) => {
    try {
      const dashboardCountries = countryCodes.join(",");
      const response = await postDataWAuth(
        `${UPDATE_DASHBOARD_PREFERENCE}?dashboardCountries=${encodeURIComponent(dashboardCountries)}`,
        null
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update country preferences");
    }
  }
);

export const updateDealerPreferences = createAsyncThunk(
  "bpDashboard/updateDealerPreferences",
  async ({ dealerIds, country }, { rejectWithValue }) => {
    try {
      const dealers = dealerIds.join(",");
      const response = await postDataWAuth(
        `${UPDATE_DEALER_PREFERENCE}?dealers=${encodeURIComponent(dealers)}&country=${encodeURIComponent(country)}`,
        null
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update dealer preferences");
    }
  }
);

const initialState = {
  extensionRequests: [],
  pendingErrors: [],
  pendingBPCheck: [],
  loading: false,
  error: null,
  dealerTrends: {
    period: "3M",
    view: "amount",
    comparisonType: "MOM",
    selectedDealers: [],
  },
};

const bpDashboardSlice = createSlice({
  name: "bpDashboard",
  initialState,
  reducers: {
    clearBPDashboardError: (state) => {
      state.error = null;
    },

    resetBPDashboard: () => initialState,

    setDealerTrendsPeriod: (state, action) => {
      state.dealerTrends.period = action.payload;
    },
    setDealerTrendsView: (state, action) => {
      state.dealerTrends.view = action.payload;
    },
    setDealerTrendsComparisonType: (state, action) => {
      state.dealerTrends.comparisonType = action.payload;
    },
    setSelectedDealers: (state, action) => {
      state.dealerTrends.selectedDealers = action.payload;
    },
    addDealers: (state, action) => {
      state.dealerTrends.selectedDealers.push(...action.payload);
    },
    removeDealer: (state, action) => {
      state.dealerTrends.selectedDealers = state.dealerTrends.selectedDealers.filter(
        (dealer) => dealer.dealerId !== action.payload
      );
    },
    clearSelectedDealers: (state) => {
      state.dealerTrends.selectedDealers = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBPDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBPDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.extensionRequests = action.payload.extensionRequests;
        state.pendingErrors = action.payload.pendingErrors;
        state.pendingBPCheck = action.payload.pendingBPCheck;
      })
      .addCase(fetchBPDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchDashboardCountryPreferences.pending, (state) => {
      })
      .addCase(fetchDashboardCountryPreferences.fulfilled, (state, action) => {
      })
      .addCase(fetchDashboardCountryPreferences.rejected, (state, action) => {
      });

    builder
      .addCase(updateDashboardCountryPreferences.pending, (state) => {
      })
      .addCase(updateDashboardCountryPreferences.fulfilled, (state, action) => {
      })
      .addCase(updateDashboardCountryPreferences.rejected, (state, action) => {
      });

    builder
      .addCase(updateDealerPreferences.pending, (state) => {
      })
      .addCase(updateDealerPreferences.fulfilled, (state, action) => {
      })
      .addCase(updateDealerPreferences.rejected, (state, action) => {
      });
  },
});

export const {
  clearBPDashboardError,
  resetBPDashboard,
  setDealerTrendsPeriod,
  setDealerTrendsView,
  setDealerTrendsComparisonType,
  setSelectedDealers,
  addDealers,
  removeDealer,
  clearSelectedDealers,
} = bpDashboardSlice.actions;

export default bpDashboardSlice.reducer;
